import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { DoseTargetData } from '~/utils/transforms/doseTargetsTransforms';
import transformStructuresToDoseTargets from '~/utils/transforms/doseTargetsTransforms';
import transformSessionStructuresToDoseTargets from '~/utils/transforms/sessionStructuresToDoseTargets';

/**
 * Local-state hook for the Dose+ prediction configuration tab.
 *
 * Target selections are held in component-local state (not in a global atom)
 * and synced to {@link dosePredictionTaskState} so the submission handler can
 * read them. Each time the tab mounts or the structure set changes, targets
 * are re-derived from the structures with their default selections.
 */
const useDoseTargetData = () => {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const sessionData = useRecoilValue(sessionDataAtom);
    const setDosePredictionTask = useSetRecoilState(dosePredictionTaskState);

    // Base targets derived from the current structure set or session data
    const baseTargets = useMemo(() => {
        if (currentStructureSet?.roiArray) {
            return transformStructuresToDoseTargets(
                currentStructureSet.roiArray
            );
        }

        const sessionStructures =
            sessionData?.payload?.context_structure_set?.structures;

        if (sessionStructures?.length) {
            return transformSessionStructuresToDoseTargets(sessionStructures);
        }

        return [];
    }, [currentStructureSet, sessionData]);

    // ── Local target state for this tab ────────────────────────────────
    const [localTargets, setLocalTargets] = useState<DoseTargetData[]>([]);

    // Re-initialize local targets whenever the underlying structures change
    useEffect(() => {
        if (baseTargets.length > 0) {
            setLocalTargets(baseTargets);
        }
    }, [baseTargets]);

    // Keep dosePredictionTaskState.targets in sync so submission reads the
    // latest values.
    useEffect(() => {
        if (localTargets.length > 0) {
            setDosePredictionTask((prev) => ({
                ...prev,
                targets: localTargets,
            }));
        }
    }, [localTargets, setDosePredictionTask]);

    const handleTargetChange = useCallback((index: number) => {
        setLocalTargets((prev) => {
            const updated = [...prev];
            const isTarget = !updated[index].isTarget;

            updated[index] = {
                ...updated[index],
                isTarget,
                doseQuantity: isTarget
                    ? Math.max(updated[index].doseQuantity, 30) // Ensure min dose of 30 Gy
                    : 0.0,
            };

            return updated;
        });
    }, []);

    const handleDoseQuantityChange = useCallback(
        (index: number, value: number | null) => {
            if (value == null) {
                return;
            }

            setLocalTargets((prev) => {
                const updated = [...prev];
                updated[index] = {
                    ...updated[index],
                    doseQuantity: value,
                };
                return updated;
            });
        },
        []
    );

    return {
        doseTargetData: localTargets,
        handleTargetChange,
        handleDoseQuantityChange,
    };
};

export default useDoseTargetData;
